import React, { useContext, useEffect, useRef, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import { useNavigate } from "react-router-dom";
import { Table, Button, Form, InputGroup, Pagination, Badge, Spinner } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import Can from "../../components/Can";
import { EditDueDateButton } from "./EditDueDate";
import { toast } from "react-toastify";
import OwnTicketStatusPicker from "./OwnTicketStatusPicker";
import CompleteTicketModal from "./CompleteTicketModal";
import TaskCreateModal from "./TaskCreateModal";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "./MyWorkPage.css";

// Phase 4A, §16/§24. ONE common page for every role (no role-specific
// duplicate pages) — "Assigned to Me" / "Performed by Me" / "Delegated to
// Me" / "Delegated by Me" are 4 tabs over 4 distinct endpoints, each
// deriving its subject from the caller's own trusted identity server-side.
const TABS = [
  { key: "assigned", label: "Assigned to Me", endpoint: "/my-assigned-tasks" },
  { key: "performed", label: "Performed by Me", endpoint: "/my-performed-tasks" },
  { key: "delegated_to", label: "Delegated to Me", endpoint: "/my-delegated-tasks" },
  { key: "delegated_by", label: "Delegated by Me", endpoint: "/my-delegated-by-me-tasks" },
];

// Presentational-only helper: turns a status/delegation-status value into a
// CSS-safe class suffix so a badge can pick up a themed color + dot via
// MyWorkPage.css. Purely a styling hook — does not alter what value is
// fetched, stored, or displayed anywhere.
const statusSlug = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "default";

const titleCaseStatus = (value) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// "18 Oct 2026" -- fixed day/month/year order so the Due Date and Created
// columns read the same on every browser locale.
export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const TERMINAL_TASK_STATUSES = new Set(["completed", "closed", "cancelled"]);

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Presentational due-date hint: tone + relative text rendered on its own
// line under the date (never concatenated with it). `now` is injectable
// for tests.
export const dueInfo = (dueAt, status, now = new Date()) => {
  if (!dueAt) return { tone: "none", hint: "" };
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return { tone: "none", hint: "" };
  if (TERMINAL_TASK_STATUSES.has(String(status || "").toLowerCase())) return { tone: "done", hint: "" };
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(due) - startOfDay(now)) / 86400000);
  if (days < 0) return { tone: "overdue", hint: `Overdue by ${plural(-days, "day")}` };
  if (days === 0) return { tone: "soon", hint: "Due today" };
  if (days === 1) return { tone: "soon", hint: "Due tomorrow" };
  if (days <= 3) return { tone: "soon", hint: `In ${plural(days, "day")}` };
  return { tone: "upcoming", hint: `In ${plural(days, "day")}` };
};

// Summary cards for My Tasks. Each count is the backend's own meta.total
// for an exact status match (GET /my-assigned-tasks?status=...&pageSize=1),
// with the same non-status filters as the table -- never a client-side
// tally of the current page, so the numbers cannot be misleading.
const STAT_STATUSES = ["pending", "assigned", "in_progress", "completed"];

// Splits leading "[LMS86][BE]" tags off a title so they render as small
// chips; the full original title stays available as the tooltip.
const splitTitleTags = (title) => {
  const raw = String(title || "");
  const lead = raw.match(/^(\s*\[[^\]]+\])+/);
  if (!lead) return { tags: [], text: raw };
  const tags = (lead[0].match(/\[([^\]]+)\]/g) || []).map((t) => t.slice(1, -1).trim()).filter(Boolean);
  const text = raw.slice(lead[0].length).trim();
  return text ? { tags, text } : { tags: [], text: raw };
};

// Avatar letter for a task title: skips leading tags like "[LMS86][BE]" so
// the badge shows a real letter instead of "[".
const titleInitial = (title) => {
  const stripped = String(title || "").replace(/^(\s*\[[^\]]*\])+\s*/, "");
  const match = stripped.match(/[A-Za-z0-9]/) || String(title || "").match(/[A-Za-z0-9]/);
  return match ? match[0].toUpperCase() : "T";
};

export default function MyWorkPage({ assignedOnly = false }) {
  const { user } = useContext(AuthContext) || { user: null };
  const permissionCtx = usePermissions() || {};
  const navigate = useNavigate();

  const [tab, setTab] = useState("assigned");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState("");
  const [taskTypeId, setTaskTypeId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectModuleId, setProjectModuleId] = useState("");
  const [priority, setPriority] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [taskTypes, setTaskTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [stats, setStats] = useState(null);
  // Column-header sorting (My Tasks only) -- applied server-side so it
  // covers every page, not just the 20 rows currently loaded.
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const statsRequestId = useRef(0);
  const pageSize = 20;

  const activeEndpoint = assignedOnly ? "/my-assigned-tasks" : TABS.find((t) => t.key === tab)?.endpoint;

  // Every filter except status (and pagination). Shared by the table fetch
  // and the summary-card counts so both always describe the same task set.
  const nonStatusFilters = {
    task_type_id: taskTypeId || undefined,
    project_id: projectId || undefined,
    project_module_id: projectModuleId || undefined,
    priority: priority || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    search: search || undefined,
  };

  const hasActiveFilters = Boolean(status || taskTypeId || projectId || projectModuleId || priority || dateFrom || dateTo || search);

  const clearFilters = () => {
    setStatus("");
    setTaskTypeId("");
    setProjectId("");
    setProjectModuleId("");
    setPriority("");
    setDateFrom("");
    setDateTo("");
    setSearch("");
    setCurrentPage(1);
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.get(activeEndpoint, {
        params: {
          status: status || undefined,
          ...nonStatusFilters,
          sort_by: assignedOnly && sortBy ? sortBy : undefined,
          sort_dir: assignedOnly && sortBy ? sortDir : undefined,
          page: currentPage,
          pageSize,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch my-work error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch your tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!assignedOnly) return;
    const requestId = ++statsRequestId.current;
    try {
      const countFor = (statusValue) =>
        api
          .get("/my-assigned-tasks", { params: { ...nonStatusFilters, status: statusValue, page: 1, pageSize: 1 } })
          .then((res) => Number(res.data?.meta?.total) || 0);
      const [all, ...byStatus] = await Promise.all([countFor(undefined), ...STAT_STATUSES.map(countFor)]);
      if (requestId !== statsRequestId.current) return;
      const counts = Object.fromEntries(STAT_STATUSES.map((s, i) => [s, byStatus[i]]));
      setStats({
        total: all,
        pending: counts.pending + counts.assigned,
        inProgress: counts.in_progress,
        completed: counts.completed,
      });
    } catch (err) {
      // Fail to "—" rather than showing a partial/incorrect count.
      if (requestId === statsRequestId.current) setStats(null);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchRows(), fetchStats()]);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, taskTypeId, projectId, projectModuleId, priority, dateFrom, dateTo, search, currentPage, sortBy, sortDir]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedOnly, taskTypeId, projectId, projectModuleId, priority, dateFrom, dateTo, search]);

  useEffect(() => {
    Promise.all([
      api.get("/task-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
    ]).then(([ttRes, pRes]) => {
      setTaskTypes(Array.isArray(ttRes?.data?.data) ? ttRes.data.data : []);
      setProjects(Array.isArray(pRes?.data?.data) ? pRes.data.data : []);
    });
  }, []);

  useEffect(() => {
    if (!projectId) { setProjectModules([]); return; }
    api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } })
      .then((res) => setProjectModules(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setProjectModules([]));
  }, [projectId]);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const switchTab = (key) => { setTab(key); setCurrentPage(1); };
  const rangeStart = total > 0 && rows.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = rangeStart > 0 ? rangeStart + rows.length - 1 : 0;
  const statValue = (key) => (stats ? stats[key] : "—");

  // Click cycles a column: ascending -> descending -> back to default order.
  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortBy("");
      setSortDir("asc");
    }
  };

  const SortableTh = ({ sortKey, children, className }) => {
    if (!assignedOnly) return <th className={className}>{children}</th>;
    const active = sortBy === sortKey;
    const icon = active ? (sortDir === "asc" ? "fa-arrow-up" : "fa-arrow-down") : "fa-sort";
    return (
      <th
        className={`mywork-sortable${active ? " mywork-sortable-active" : ""}${className ? ` ${className}` : ""}`}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        <button type="button" className="mywork-sort-btn" onClick={() => handleSort(sortKey)} title={`Sort by ${children}`}>
          <span>{children}</span>
          <i className={`fa-solid ${icon}`} aria-hidden="true"></i>
        </button>
      </th>
    );
  };

  const statCards = assignedOnly
    ? [
        { key: "total", label: "Total Tasks", tone: "blue", icon: "fa-list-check" },
        { key: "pending", label: "Pending", tone: "amber", icon: "fa-hourglass-half", title: "Pending + Assigned" },
        { key: "inProgress", label: "In Progress", tone: "purple", icon: "fa-spinner" },
        { key: "completed", label: "Completed", tone: "green", icon: "fa-circle-check" },
      ]
    : [
        { key: "total", label: "Total Tasks", tone: "blue", icon: "fa-list-check", value: total },
        { key: "page", label: "Current Page", tone: "purple", icon: "fa-file-lines", value: currentPage },
        { key: "size", label: "Rows Per Page", tone: "green", icon: "fa-table-list", value: pageSize },
      ];

  return (
    <div className="unit-page mywork-theme">

      <div className="mywork-heading-box">
        <div className="mywork-heading-content">
          <div className="mywork-heading-icon">
            <i className="fa-solid fa-list-check" aria-hidden="true"></i>
          </div>

          <div>
            <div className="unit-page-label">Tasks</div>
            <h2>{assignedOnly ? "My Tasks" : "My Work"}</h2>
            <p>
              {assignedOnly
                ? "Manage tasks assigned to you, including project and milestone work."
                : "Everything you're officially assigned, performing, or involved in through delegation."}
            </p>
          </div>
        </div>
        <div className="unit-header-actions">
          {/* Visibility only -- POST /tasks/create re-checks tasks:create
              server-side. Hidden (not disabled) without it. Opens the shared
              TaskCreateModal in place instead of leaving the page. */}
          {assignedOnly && user?.role && (
            <Can moduleKey="tasks" actionKey="create">
              <Button className="unit-btn mywork-add-btn" onClick={() => setShowCreateModal(true)}>
                <i className="fa-solid fa-plus" aria-hidden="true"></i>
                Add Task
              </Button>
            </Can>
          )}
          <Button className="unit-btn unit-btn-light" onClick={refreshAll}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>
        </div>
      </div>

      <div className={`unit-stats-row mywork-stats-row${statCards.length === 4 ? " mywork-stats-row-4" : ""}`}>
        {statCards.map((card) => (
          <div key={card.key} className={`unit-stat-card mywork-stat-card mywork-stat-${card.tone}`} title={card.title} data-testid={`mywork-stat-${card.key}`}>
            <div className="mywork-stat-content">
              <div className="mywork-stat-text">
                <span>{card.label}</span>
                <strong>{card.value !== undefined ? card.value : statValue(card.key)}</strong>
              </div>
              <div className="mywork-stat-icon">
                <i className={`fa-solid ${card.icon}`} aria-hidden="true"></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="unit-card">
        <div className="unit-toolbar mywork-toolbar">
          <div className="mywork-section-title">
            <div className="mywork-section-icon">
              <i className="fa-solid fa-table-list" aria-hidden="true"></i>
            </div>
            <div className="mywork-section-copy">
              <h5>{assignedOnly ? "Task List" : "Work Queue"}</h5>
              <p>
                {assignedOnly
                  ? "Search and filter the tasks assigned to you, then open a task to review or complete it."
                  : "Search and filter your assigned, performed and delegated work."}
              </p>
            </div>
          </div>

          {!assignedOnly && (
            <div className="unit-tabs">
              {TABS.map((t) => (
                <Button
                  key={t.key}
                  size="sm"
                  variant={tab === t.key ? "primary" : "outline-secondary"}
                  className={`unit-tab${tab === t.key ? " unit-tab-active" : ""}`}
                  onClick={() => switchTab(t.key)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          )}

          <div className="unit-toolbar-actions mywork-filters-row">
            <InputGroup className="unit-search mywork-filter-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control placeholder="Search task..." aria-label="Search tasks" value={search} onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }} />
            </InputGroup>
            <Form.Select aria-label="Filter by status" value={status} onChange={(e) => { setCurrentPage(1); setStatus(e.target.value); }}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="development_started">Development Started</option>
              <option value="development_completed">Development Completed</option>
              <option value="testing">Testing</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
            <Form.Select aria-label="Filter by priority" value={priority} onChange={(e) => { setCurrentPage(1); setPriority(e.target.value); }}>
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </Form.Select>
            <Form.Select aria-label="Filter by task type" value={taskTypeId} onChange={(e) => { setCurrentPage(1); setTaskTypeId(e.target.value); }}>
              <option value="">All Task Types</option>
              {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Form.Select>
            <Form.Select aria-label="Filter by project" value={projectId} onChange={(e) => { setCurrentPage(1); setProjectId(e.target.value); setProjectModuleId(""); }}>
              <option value="">All Projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Form.Select>
            <Form.Select aria-label="Filter by module" value={projectModuleId} onChange={(e) => { setCurrentPage(1); setProjectModuleId(e.target.value); }} disabled={!projectId}>
              <option value="">All Modules</option>
              {projectModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Form.Select>
            <InputGroup className="mywork-filter-date">
              <InputGroup.Text>From</InputGroup.Text>
              <Form.Control type="date" aria-label="From date" value={dateFrom} onChange={(e) => { setCurrentPage(1); setDateFrom(e.target.value); }} />
            </InputGroup>
            <InputGroup className="mywork-filter-date">
              <InputGroup.Text>To</InputGroup.Text>
              <Form.Control type="date" aria-label="To date" value={dateTo} onChange={(e) => { setCurrentPage(1); setDateTo(e.target.value); }} />
            </InputGroup>
            {hasActiveFilters && (
              <Button type="button" variant="link" className="mywork-clear-filters" onClick={clearFilters}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader"><Spinner animation="border" /><p>Loading...</p></div>
          ) : (
            <Table responsive className={`unit-table mywork-table mywork-table-${tab}${assignedOnly ? " mywork-table-rich" : ""}`}>
              <thead>
                <tr>
                  <SortableTh sortKey="title">Task</SortableTh>
                  {assignedOnly && <SortableTh sortKey="project">Project / Module / Milestone</SortableTh>}
                  <SortableTh sortKey="status">Status</SortableTh>
                  <SortableTh sortKey="priority">Priority</SortableTh>
                  {tab === "assigned" && <SortableTh sortKey="assigned_to">Assigned To</SortableTh>}
                  {tab === "assigned" && <SortableTh sortKey="staff">Staff</SortableTh>}
                  {assignedOnly && <SortableTh sortKey="due_at">Due Date</SortableTh>}
                  {tab === "performed" && <><th>Role</th><th>Contribution</th></>}
                  {(tab === "delegated_to" || tab === "delegated_by") && <><th>Type</th><th>{tab === "delegated_to" ? "Delegated By" : "Delegated To"}</th><th>Delegation Status</th></>}
                  <SortableTh sortKey="created_at">Created</SortableTh>
                  <th className="text-end mywork-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? rows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Task">
                      <div className="unit-name-box">
                        <div className={`unit-letter mywork-task-avatar mywork-task-avatar-${statusSlug(splitTitleTags(taskDisplayTitle(row)).tags.slice(-1)[0] || "default")}`}>{titleInitial(taskDisplayTitle(row))}</div>
                        <div className="mywork-title-cell">
                          {(() => {
                            const { tags, text } = splitTitleTags(taskDisplayTitle(row));
                            return (
                              <>
                                <strong title={taskDisplayTitle(row)}>{text}</strong>
                                <div className="mywork-task-meta">
                                  <span className="mywork-task-id">#{row.id}</span>
                                  {tags.map((tag, i) => (
                                    <span key={`${tag}-${i}`} className={`mywork-title-tag mywork-title-tag-${statusSlug(tag)}`}>{tag}</span>
                                  ))}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    {assignedOnly && (
                      <td data-label="Project / Module / Milestone">
                        <div className="mywork-project-context">
                          <strong>{row.project_name || "—"}</strong>
                          {(row.project_module_name || row.project_milestone_name) && (
                            <span>
                              {[row.project_module_name, row.project_milestone_name].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td data-label="Status">
                      {assignedOnly ? (
                        <OwnTicketStatusPicker
                          row={row}
                          onChanged={refreshAll}
                          onCompleteRequested={(task) => setCompleteTarget(task)}
                          allowDirectComplete
                          allowDirectInProgress
                        />
                      ) : (
                        <Badge bg="light" text="dark" className={`mywork-status-badge mywork-status-${statusSlug(row.status)}`}>{titleCaseStatus(row.status)}</Badge>
                      )}
                    </td>
                    <td data-label="Priority"><Badge bg="light" text="dark" className={`mywork-badge mywork-priority-${statusSlug(row.priority)}`}>{row.priority || "—"}</Badge></td>
                    {tab === "assigned" && (
                      <td className="small" data-label="Assigned To">
                        {row.assigned_to_name ? (
                          <div className="unit-name-box">
                            <div className="unit-letter">{row.assigned_to_name.charAt(0).toUpperCase()}</div>
                            <div><strong>{row.assigned_to_name}</strong></div>
                          </div>
                        ) : "—"}
                      </td>
                    )}
                    {tab === "assigned" && (
                      <td className="small" data-label="Staff">
                        {row.requested_for_name ? (
                          <div className="unit-name-box">
                            <div className="unit-letter">{row.requested_for_name.charAt(0).toUpperCase()}</div>
                            <div><strong>{row.requested_for_name}</strong></div>
                          </div>
                        ) : "—"}
                      </td>
                    )}
                    {assignedOnly && (
                      <td className="small" data-label="Due Date">
                        {(() => {
                          const { tone, hint } = dueInfo(row.due_at, row.status);
                          return (
                            <div className={`mywork-due mywork-due-${tone}`}>
                              <span className="mywork-due-date">
                                {formatDate(row.due_at)}
                                {/* Assignee-only, open tasks only; the row updates in place. */}
                                <EditDueDateButton
                                  task={row}
                                  user={user}
                                  can={permissionCtx?.can}
                                  onUpdated={(dueAt) => setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, due_at: dueAt } : r)))}
                                />
                              </span>
                              {hint ? <span className={`mywork-due-hint${tone === "overdue" ? " mywork-due-overdue" : ""}`}>{hint}</span> : null}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {tab === "performed" && (
                      <>
                        <td data-label="Role"><Badge bg="light" text="dark" className="mywork-badge">{row.performer_role}</Badge></td>
                        <td className="small" data-label="Contribution">{row.contribution_percentage != null ? `${row.contribution_percentage}%` : "—"}</td>
                      </>
                    )}
                    {tab === "delegated_to" && (
                      <>
                        <td data-label="Type"><Badge bg="light" text="dark" className="mywork-badge">{row.delegation_type?.replace("_", " ")}</Badge></td>
                        <td className="small" data-label="Delegated By">
                          {row.delegated_by_name ? (
                            <div className="unit-name-box">
                              <div className="unit-letter">{row.delegated_by_name.charAt(0).toUpperCase()}</div>
                              <div><strong>{row.delegated_by_name}</strong></div>
                            </div>
                          ) : row.delegated_by_name}
                        </td>
                        <td data-label="Delegation Status"><Badge bg="success" className={`mywork-status-badge mywork-status-${statusSlug(row.delegation_status)}`}>{row.delegation_status}</Badge></td>
                      </>
                    )}
                    {tab === "delegated_by" && (
                      <>
                        <td data-label="Type"><Badge bg="light" text="dark" className="mywork-badge">{row.delegation_type?.replace("_", " ")}</Badge></td>
                        <td className="small" data-label="Delegated To">
                          {row.delegated_to_name ? (
                            <div className="unit-name-box">
                              <div className="unit-letter">{row.delegated_to_name.charAt(0).toUpperCase()}</div>
                              <div><strong>{row.delegated_to_name}</strong></div>
                            </div>
                          ) : row.delegated_to_name}
                        </td>
                        <td data-label="Delegation Status"><Badge bg="light" text="dark" className={`mywork-status-badge mywork-status-${statusSlug(row.delegation_status)}`}>{row.delegation_status}</Badge></td>
                      </>
                    )}
                    <td className="small mywork-created-cell" data-label="Created">{formatDate(row.created_at)}</td>
                    <td className="text-end mywork-actions-col" data-label="Action">
                      <div className="mywork-row-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="mywork-action-icon mywork-view-btn"
                          onClick={() => navigate(`/${user?.role}/tasks/${row.id}`)}
                          aria-label={`View details for ${taskDisplayTitle(row) || "task"}`}
                          title="View details"
                        >
                          <i className="fa-solid fa-eye" aria-hidden="true"></i>
                        </Button>

                        {assignedOnly && row.project_id && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="mywork-action-icon mywork-project-btn"
                            onClick={() =>
                              navigate(
                                `/${user?.role}/projects/${row.project_id}?tab=${
                                  row.project_milestone_id ? "milestones" : "overview"
                                }`
                              )
                            }
                            aria-label={`Open project for ${taskDisplayTitle(row) || "task"}`}
                            title={row.project_milestone_id ? "Open project milestones" : "Open project"}
                          >
                            <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
                          </Button>
                        )}

                        {assignedOnly && row.project_id && row.active_performer_id && (
                          <Button
                            size="sm"
                            variant="outline-dark"
                            className="mywork-action-icon mywork-worklog-btn"
                            onClick={() =>
                              navigate(`/${user?.role}/projects/${row.project_id}?tab=daily-work`)
                            }
                            aria-label={`Record daily work for ${taskDisplayTitle(row) || "task"}`}
                            title="Record daily work"
                          >
                            <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i>
                          </Button>
                        )}

                        {assignedOnly &&
                          row.project_id &&
                          !TERMINAL_TASK_STATUSES.has(String(row.status || "").toLowerCase()) && (
                            <Button
                              size="sm"
                              variant="success"
                              className="mywork-action-icon mywork-complete-btn"
                              onClick={() => setCompleteTarget(row)}
                              aria-label={`Complete ${taskDisplayTitle(row) || "task"}`}
                              title="Complete task"
                            >
                              <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={assignedOnly ? 9 : tab === "assigned" || tab === "performed" ? 7 : 8}>
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-inbox" aria-hidden="true"></i>
                        </div>
                        <h5>Nothing here</h5>
                        <p>No tasks match this view and the selected filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing <strong>{rangeStart > 0 ? `${rangeStart}–${rangeEnd}` : 0}</strong> of <strong>{total}</strong>{" "}
            {assignedOnly ? (total === 1 ? "task" : "tasks") : "item(s)"}
          </div>
          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} />
              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>
              )), currentPage)}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
            </Pagination>
          )}
        </div>
      </div>

      <CompleteTicketModal
        show={Boolean(completeTarget)}
        task={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onCompleted={async () => {
          setCompleteTarget(null);
          await refreshAll();
        }}
      />

      {assignedOnly && (
        <TaskCreateModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={refreshAll}
        />
      )}
    </div>
  );
}
